import { ref, computed, watch, onBeforeUnmount, toValue, type MaybeRefOrGetter } from 'vue'
import { useEditorStore } from '@/stores/editor'
import { useSnackbarStore } from '@/stores/snackbar'
import { http } from '@/helpers/http'
import { track } from '@/helpers/mixpanelDes'
import type { TDropdownOption } from '@/types/dropdown'

const STORAGE_KEY = 'stt-language'
const USAGE_REPORT_INTERVAL_MS = 30_000
const DEEPGRAM_WS_URL = 'wss://api.deepgram.com/v1/listen'
const SILENCE_AUTO_STOP_MS = 30_000

const languages: TDropdownOption[] = [
  { name: 'ar', label: '🇸🇦 Arabic' },
  { name: 'bn', label: '🇧🇩 Bengali' },
  { name: 'bg', label: '🇧🇬 Bulgarian' },
  { name: 'ca', label: '🏴 Catalan' },
  { name: 'hr', label: '🇭🇷 Croatian' },
  { name: 'cs', label: '🇨🇿 Czech' },
  { name: 'da-DK', label: '🇩🇰 Danish' },
  { name: 'nl', label: '🇳🇱 Dutch' },
  { name: 'en-AU', label: '🇦🇺 English (AU)' },
  { name: 'en-IN', label: '🇮🇳 English (India)' },
  { name: 'en-NZ', label: '🇳🇿 English (NZ)' },
  { name: 'en-GB', label: '🇬🇧 English (UK)' },
  { name: 'en-US', label: '🇺🇸 English (US)' },
  { name: 'et', label: '🇪🇪 Estonian' },
  { name: 'fi', label: '🇫🇮 Finnish' },
  { name: 'fr', label: '🇫🇷 French' },
  { name: 'fr-CA', label: '🇨🇦 French (Canada)' },
  { name: 'de', label: '🇩🇪 German' },
  { name: 'el', label: '🇬🇷 Greek' },
  { name: 'he', label: '🇮🇱 Hebrew' },
  { name: 'hi', label: '🇮🇳 Hindi' },
  { name: 'hu', label: '🇭🇺 Hungarian' },
  { name: 'id', label: '🇮🇩 Indonesian' },
  { name: 'it', label: '🇮🇹 Italian' },
  { name: 'ja', label: '🇯🇵 Japanese' },
  { name: 'ko-KR', label: '🇰🇷 Korean' },
  { name: 'lv', label: '🇱🇻 Latvian' },
  { name: 'lt', label: '🇱🇹 Lithuanian' },
  { name: 'ms', label: '🇲🇾 Malay' },
  { name: 'no', label: '🇳🇴 Norwegian' },
  { name: 'fa', label: '🇮🇷 Persian' },
  { name: 'pl', label: '🇵🇱 Polish' },
  { name: 'pt-BR', label: '🇧🇷 Portuguese (Brazil)' },
  { name: 'pt-PT', label: '🇵🇹 Portuguese (Portugal)' },
  { name: 'ro', label: '🇷🇴 Romanian' },
  { name: 'ru', label: '🇷🇺 Russian' },
  { name: 'sr', label: '🇷🇸 Serbian' },
  { name: 'sk', label: '🇸🇰 Slovak' },
  { name: 'sl', label: '🇸🇮 Slovenian' },
  { name: 'es', label: '🇪🇸 Spanish' },
  { name: 'es-419', label: '🌎 Spanish (Latin America)' },
  { name: 'sv-SE', label: '🇸🇪 Swedish' },
  { name: 'tl', label: '🇵🇭 Tagalog' },
  { name: 'ta', label: '🇮🇳 Tamil' },
  { name: 'te', label: '🇮🇳 Telugu' },
  { name: 'tr', label: '🇹🇷 Turkish' },
  { name: 'uk', label: '🇺🇦 Ukrainian' },
  { name: 'ur', label: '🇵🇰 Urdu' },
  { name: 'vi', label: '🇻🇳 Vietnamese' },
]

type TokenResponse = {
  token: string
  expires_in: number
  seconds_left: number
}

type UsageResponse = {
  seconds_left: number
}

function getSavedLanguage(): TDropdownOption {
  const saved = localStorage.getItem(STORAGE_KEY)
  return languages.find((l) => l.name === saved) ?? languages.find((l) => l.name === 'en-US')!
}

// Module-scope singleton so every useSpeechToText() consumer (wizard chat input,
// floating editor widget, future surfaces) shares the same reactive selection.
// Writes from one surface — manual dropdown change, or the wizard's
// applyPreferredSttLanguage sync — are visible everywhere without a reload.
const selectedLanguage = ref<TDropdownOption>(getSavedLanguage())

export function microphoneErrorMessage(errorName: string): string {
  if (errorName === 'NotFoundError') {
    return 'No microphone was found. Connect a microphone and try again.'
  }

  if (errorName === 'NotReadableError') {
    return 'The microphone is being used by another application. Close it and try again.'
  }

  return 'Microphone access is blocked. Please allow it in your browser settings.'
}

export function findDeepgramLanguage(bcp47: string): TDropdownOption | null {
  const direct = languages.find((l) => l.name.toLowerCase() === bcp47.toLowerCase())
  if (direct) {
    return direct
  }
  const prefix = bcp47.split('-')[0].toLowerCase()
  return languages.find((l) => l.name.toLowerCase() === prefix) ?? null
}

export function applyPreferredSttLanguage(bcp47: string): boolean {
  const match = findDeepgramLanguage(bcp47)
  if (!match) {
    return false
  }
  selectedLanguage.value = match
  localStorage.setItem(STORAGE_KEY, match.name)
  return true
}

export function useSpeechToText(draftType?: MaybeRefOrGetter<string | undefined>, onInsert?: (text: string) => void) {
  const editorStore = useEditorStore()
  const snack = useSnackbarStore()

  const userWantsListening = ref(false)
  const isListening = ref(false)
  const errorMessage = ref('')
  const interimText = ref('')
  const secondsLeft = ref<number | null>(null)

  const isSupported = computed(() => {
    return typeof WebSocket !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  })

  let ws: WebSocket | null = null
  let mediaStream: MediaStream | null = null
  let audioContext: AudioContext | null = null
  let unreportedSeconds = 0
  let recordingStartedAt = 0
  let usageReportTimer: ReturnType<typeof setInterval> | null = null
  let cachedToken: TokenResponse | null = null
  let cachedTokenAt = 0
  let lastInsertedText = ''
  let sessionStartedAt = 0
  let utteranceEndPending = false
  let silenceTimer: ReturnType<typeof setTimeout> | null = null
  let editorLockedElement: HTMLElement | null = null

  function blockKeydown(e: KeyboardEvent): void {
    e.preventDefault()
    e.stopPropagation()
  }

  function blockMousedown(e: MouseEvent): void {
    e.preventDefault()
    e.stopPropagation()
  }

  function lockEditor(): void {
    const ed = editorStore.getEditor()
    if (!ed) {
      return
    }
    editorLockedElement = ed.view.dom
    editorLockedElement.addEventListener('keydown', blockKeydown, true)
    editorLockedElement.addEventListener('mousedown', blockMousedown, true)
    editorLockedElement.style.userSelect = 'none'
  }

  function unlockEditor(): void {
    if (!editorLockedElement) {
      return
    }
    editorLockedElement.removeEventListener('keydown', blockKeydown, true)
    editorLockedElement.removeEventListener('mousedown', blockMousedown, true)
    editorLockedElement.style.userSelect = ''
    editorLockedElement = null
  }

  async function requestToken(): Promise<TokenResponse | null> {
    const now = Date.now()
    if (cachedToken && now - cachedTokenAt < 180_000 && cachedToken.seconds_left > 0) {
      return cachedToken
    }
    try {
      const res = await http.post<TokenResponse>('/api/stt/request-key')
      cachedToken = res
      cachedTokenAt = Date.now()
      return res
    } catch (e: any) {
      if (e?.status === 429) {
        errorMessage.value = 'Please wait a moment before starting again.'
        snack.add(errorMessage.value, { type: 'danger' })
      } else if (e?.status === 409) {
        const fallback = 'Voice typing is not available for your account.'
        try {
          errorMessage.value = JSON.parse(e.response)?.error ?? fallback
        } catch {
          errorMessage.value = fallback
        }
        snack.add(errorMessage.value, { type: 'danger' })
      } else {
        errorMessage.value = 'Failed to start voice typing. Please try again.'
        snack.add(errorMessage.value, { type: 'danger' })
      }
      return null
    }
  }

  async function reportUsage(): Promise<void> {
    if (unreportedSeconds <= 0) {
      return
    }
    const seconds = Math.round(unreportedSeconds)
    unreportedSeconds = 0
    try {
      const res = await http.post<UsageResponse>('/api/stt/report-usage', { seconds })
      secondsLeft.value = res.seconds_left
    } catch {
      // Best-effort, don't block the user
    }
  }

  function startUsageTracking(): void {
    recordingStartedAt = Date.now()
    usageReportTimer = setInterval(() => {
      const now = Date.now()
      unreportedSeconds += (now - recordingStartedAt) / 1000
      recordingStartedAt = now
      reportUsage()
    }, USAGE_REPORT_INTERVAL_MS)
  }

  function stopUsageTracking(): void {
    if (usageReportTimer) {
      clearInterval(usageReportTimer)
      usageReportTimer = null
    }
    if (recordingStartedAt > 0) {
      unreportedSeconds += (Date.now() - recordingStartedAt) / 1000
      recordingStartedAt = 0
    }
    reportUsage()
  }

  function buildWsUrl(): string {
    const lang = selectedLanguage.value.name
    const params = new URLSearchParams({
      model: 'nova-3',
      language: lang,
      smart_format: 'true',
      interim_results: 'true',
      punctuate: 'true',
      encoding: 'linear16',
      sample_rate: '16000',
      utterance_end_ms: '1000',
      vad_events: 'true',
    })
    return `${DEEPGRAM_WS_URL}?${params.toString()}`
  }

  function endsWithSentencePunctuation(text: string): boolean {
    return /[.!?][\s]*$/.test(text.trim())
  }

  function insertText(text: string): void {
    if (onInsert) {
      if (!text.trim()) {
        return
      }
      onInsert(text + ' ')
      utteranceEndPending = false
      lastInsertedText = text
      return
    }
    const ed = editorStore.getEditor()
    if (!ed || !text.trim()) {
      return
    }
    const from = ed.state.selection.from

    if (utteranceEndPending && endsWithSentencePunctuation(lastInsertedText)) {
      ed.chain()
        .focus()
        .splitBlock()
        .insertContent(text + ' ')
        .run()
    } else {
      ed.chain()
        .focus()
        .insertContent(text + ' ')
        .run()
    }

    const to = ed.state.selection.from

    ed.chain().setTextSelection({ from, to }).setMark('speechHighlight').setTextSelection(to).run()
    setTimeout(() => {
      const ed2 = editorStore.getEditor()
      if (!ed2) return

      const { tr } = ed2.state
      const markType = ed2.schema.marks.speechHighlight

      if (markType) {
        tr.removeMark(from, to, markType)
        ed2.view.dispatch(tr)
      }
    }, 3000)
    utteranceEndPending = false
    lastInsertedText = text
  }

  function startSilenceTimer(): void {
    clearSilenceTimer()
    silenceTimer = setTimeout(() => {
      const idleSeconds = Math.round(SILENCE_AUTO_STOP_MS / 1000)
      track('microphone-recording-auto-stopped', { idle_ms: SILENCE_AUTO_STOP_MS })
      snack.add(`Recording stopped — silence detected for ${idleSeconds} seconds.`, { type: 'danger', permanent: true })
      stopListening()
    }, SILENCE_AUTO_STOP_MS)
  }

  function clearSilenceTimer(): void {
    if (silenceTimer) {
      clearTimeout(silenceTimer)
      silenceTimer = null
    }
  }

  function cleanup(): void {
    clearSilenceTimer()
    if (audioContext) {
      audioContext.close()
      audioContext = null
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop())
      mediaStream = null
    }

    if (ws) {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close()
      }
      ws = null
    }

    if (!onInsert) {
      unlockEditor()
    }
    stopUsageTracking()
    isListening.value = false
    interimText.value = ''
    utteranceEndPending = false
    lastInsertedText = ''
  }

  async function startListening(): Promise<void> {
    if (!isSupported.value || userWantsListening.value) {
      return
    }

    errorMessage.value = ''

    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      track('microphone-recording-started', {
        language: selectedLanguage.value.label.replace(/^\p{So}+\s*/u, ''),
        draft_type: toValue(draftType),
      })
    } catch (e) {
      const reason = e instanceof Error ? e.name : 'unknown'
      snack.add(microphoneErrorMessage(reason), { type: 'danger' })
      track('microphone-recording-failed', { reason })
      return
    }

    const tokenRes = await requestToken()
    if (!tokenRes) {
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => t.stop())
        mediaStream = null
      }
      return
    }

    secondsLeft.value = tokenRes.seconds_left
    userWantsListening.value = true
    sessionStartedAt = Date.now()
    editorStore.isSpeechRecording = true
    if (!onInsert) {
      lockEditor()
    }

    const wsUrl = buildWsUrl()
    ws = new WebSocket(wsUrl, ['bearer', tokenRes.token])

    ws.onopen = () => {
      isListening.value = true
      startAudioStreaming()
      startUsageTracking()
      startSilenceTimer()
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'SpeechStarted') {
          return
        }
        if (data.type === 'UtteranceEnd') {
          utteranceEndPending = true
          startSilenceTimer()
          return
        }
        if (data.type !== 'Results') {
          return
        }
        const transcript = data.channel?.alternatives?.[0]?.transcript ?? ''
        if (data.is_final) {
          interimText.value = ''
          if (transcript.trim()) {
            clearSilenceTimer()
            insertText(transcript)
          }
        } else {
          interimText.value = transcript
        }
      } catch {
        // Ignore malformed messages
      }
    }

    ws.onerror = (event) => {
      console.error('[STT] WebSocket error:', event)
      errorMessage.value = 'Connection error. Please try again.'
      snack.add(errorMessage.value, { type: 'danger' })
      stopListening()
    }

    ws.onclose = (event) => {
      console.log('[STT] WebSocket closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean })
      if (userWantsListening.value) {
        isListening.value = false
      }
    }
  }

  function startAudioStreaming(): void {
    if (!mediaStream) {
      return
    }

    audioContext = new AudioContext({ sampleRate: 16000 })
    const source = audioContext.createMediaStreamSource(mediaStream)
    const processor = audioContext.createScriptProcessor(4096, 1, 1)

    processor.onaudioprocess = (e) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return
      }
      const inputData = e.inputBuffer.getChannelData(0)
      const pcm16 = new Int16Array(inputData.length)
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]!))
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
      ws.send(pcm16.buffer)
    }

    source.connect(processor)
    processor.connect(audioContext.destination)
  }

  function stopListening(): void {
    if (userWantsListening.value) {
      const durationSeconds = sessionStartedAt > 0 ? Math.round((Date.now() - sessionStartedAt) / 1000) : 0
      track('microphone-recording-stopped', { duration_seconds: durationSeconds })
      sessionStartedAt = 0
    }
    userWantsListening.value = false
    editorStore.isSpeechRecording = false
    cleanup()
  }

  function toggleListening(): void {
    if (userWantsListening.value) {
      stopListening()
    } else {
      startListening()
    }
  }

  function changeLanguage(lang: TDropdownOption): void {
    const wasListening = userWantsListening.value
    if (wasListening) {
      stopListening()
    }
    selectedLanguage.value = lang
    localStorage.setItem(STORAGE_KEY, lang.name)
  }

  watch(
    () => editorStore.isSpeechPanel,
    (open) => {
      if (!open) {
        stopListening()
      }
    },
  )

  onBeforeUnmount(() => {
    stopListening()
  })

  // Report usage on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (unreportedSeconds > 0 && recordingStartedAt > 0) {
        unreportedSeconds += (Date.now() - recordingStartedAt) / 1000
        recordingStartedAt = 0
        const seconds = Math.round(unreportedSeconds)
        if (seconds > 0) {
          navigator.sendBeacon(
            '/api/stt/report-usage',
            new Blob([JSON.stringify({ seconds })], { type: 'application/json' }),
          )
        }
      }
    })
  }

  return {
    isSupported,
    isListening,
    userWantsListening,
    selectedLanguage,
    languages,
    interimText,
    errorMessage,
    secondsLeft,
    startListening,
    stopListening,
    toggleListening,
    changeLanguage,
  }
}
